import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ErrorTable, { type ErrorTableColumn } from './bulk-stock-upload/ErrorTable';
import { formatCsvParseError } from '../utils/csvParse';
import { translateCsvParseError } from '../utils/csvErrorMessages';
import {
  buildHoldBackFailedRowsFilename,
  type HoldBackCsvCell,
  type HoldBackCsvRejection,
  type HoldBackCsvRejectionReason,
  type ParseHoldBackCsvResult,
} from '../utils/holdBackCsv';

/**
 * Review step between picking a CSV and staging it into the grid.
 *
 * Rejected rows are never staged and never silently dropped from view: a bulk
 * edit that quietly discarded a third of the file would be indistinguishable
 * from one that worked, and the operator would find out at the next push. They
 * render through the shared `ErrorTable` (search, full-set download, BOM-safe)
 * rather than a fifth hand-rolled copy of it.
 *
 * Staging does NOT save. Accepted cells land in the page's `pending` map through
 * its own `setCell`, and the operator then presses the existing Save, which runs
 * the untouched `HoldBackConfirmDialog` -> `previewHoldBackImpact` ->
 * `saveHoldBackRules` path. No new backend procedure exists behind this.
 *
 * EVERY BRANCH OFFERS A WAY FORWARD. A dialog whose only enabled control is
 * Cancel tells the operator they were wrong without telling them what to do
 * next, so the unusable-file branch carries "Choose another file" wired to the
 * page's own file input.
 */

/**
 * Reason code -> i18n key. Literal strings, not a template, so the i18n
 * reference scan (`scripts/i18n-check.mjs`) sees every key.
 */
const REASON_KEYS: Record<HoldBackCsvRejectionReason, string> = {
  unknown_location: 'holdBack.csv.reasonUnknownLocation',
  ambiguous_location: 'holdBack.csv.reasonAmbiguousLocation',
  unknown_production_type: 'holdBack.csv.reasonUnknownProductionType',
  unknown_product_type: 'holdBack.csv.reasonUnknownProductType',
  invalid_units: 'holdBack.csv.reasonInvalidUnits',
  no_stock: 'holdBack.csv.reasonNoStock',
};

const EMPTY_VALUE = '-';

export function HoldBackImportDialog({
  open,
  onOpenChange,
  result,
  locationNames,
  onStage,
  onChooseAnotherFile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while no file has been read yet. */
  result: ParseHoldBackCsvResult | null;
  /**
   * The location names the grid WILL accept. Named in the unknown-location
   * message so the operator can fix the file without the page having to explain
   * why a name is missing.
   */
  locationNames: string[];
  onStage: (cells: HoldBackCsvCell[]) => void;
  /** Closes this dialog and re-opens the page's file picker. */
  onChooseAnotherFile: () => void;
}) {
  const { t } = useTranslation('inventory');

  const cells = result?.cells ?? [];
  const rejected = result?.rejected ?? [];
  const fileError = result?.fileError ?? null;
  const canStage = fileError === null && cells.length > 0;

  const acceptedLocations = useMemo(() => locationNames.join(', '), [locationNames]);

  const reasonText = useCallback(
    (row: HoldBackCsvRejection): string =>
      t(REASON_KEYS[row.reason], { locations: acceptedLocations, location: row.location }),
    [t, acceptedLocations]
  );

  /**
   * The download mirrors these columns exactly. The previous hand-rolled table
   * showed four columns and downloaded six, so the file an operator worked from
   * was not the thing they had just read.
   */
  const columns = useMemo<ErrorTableColumn<HoldBackCsvRejection>[]>(
    () => [
      {
        key: 'row',
        header: t('holdBack.csv.colRow'),
        className: 'w-14',
        cellClassName: 'tabular-nums text-muted-foreground',
        cell: (row) => row.row,
        csv: (row) => row.row,
      },
      {
        key: 'location',
        header: t('holdBack.csv.colLocation'),
        cell: (row) => row.location || EMPTY_VALUE,
        csv: (row) => row.location,
        search: (row) => row.location,
      },
      {
        key: 'productionType',
        header: t('holdBack.csv.colProductionType'),
        className: 'w-24',
        cellClassName: 'uppercase text-muted-foreground',
        cell: (row) => row.productionType || EMPTY_VALUE,
        csv: (row) => row.productionType,
        search: (row) => row.productionType,
      },
      {
        key: 'productType',
        header: t('holdBack.csv.colProductType'),
        cell: (row) => row.productType || EMPTY_VALUE,
        csv: (row) => row.productType,
        search: (row) => row.productType,
      },
      {
        key: 'units',
        header: t('holdBack.csv.colUnits'),
        className: 'w-16 text-right',
        cellClassName: 'tabular-nums',
        cell: (row) => row.rawUnits || EMPTY_VALUE,
        csv: (row) => row.rawUnits,
      },
      {
        key: 'reason',
        header: t('holdBack.csv.colReason'),
        cellClassName: 'text-destructive',
        cell: (row) => reasonText(row),
        csv: (row) => reasonText(row),
        search: (row) => reasonText(row),
      },
    ],
    [t, reasonText]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{t('holdBack.csv.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('holdBack.csv.dialogDesc')}</DialogDescription>
        </DialogHeader>

        {/* ONE stable live region, mounted for the life of the dialog (mirrors
            HoldBackConfirmDialog). A role="status" inserted together with its
            text is never announced by several screen readers, which is exactly
            the case here: the counts arrive with the dialog. The file-error
            branch owns its own role="alert", so this stays empty there rather
            than double-reading. */}
        <p className="sr-only" role="status">
          {fileError
            ? ''
            : `${
                cells.length > 0
                  ? t('holdBack.csv.acceptedCount', { count: cells.length })
                  : t('holdBack.csv.noneAccepted')
              }${
                rejected.length > 0
                  ? ` ${t('holdBack.csv.rejectedCount', { count: rejected.length })}`
                  : ''
              }`}
        </p>

        <div className="space-y-4">
          {fileError ? (
            <div
              className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive"
              role="alert"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-medium">{t('holdBack.csv.fileErrorTitle')}</p>
                {fileError.code === 'too_many_cells' ? (
                  <p>
                    {t('holdBack.csv.fileErrorTooManyCells', {
                      count: fileError.count,
                      limit: fileError.limit,
                    })}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {fileError.errors.map((error, index) => (
                      <li key={index}>
                        {translateCsvParseError(error, t, formatCsvParseError(error))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm">
              {cells.length > 0
                ? t('holdBack.csv.acceptedCount', { count: cells.length })
                : t('holdBack.csv.noneAccepted')}
            </p>
          )}

          {/* OUTSIDE the file-error branch on purpose. A file can be both too
              large and full of bad rows; showing only the size sends the
              operator back to fix one problem and straight into the other. */}
          {rejected.length > 0 && (
            <div className="space-y-1.5">
              <p
                id="hold-back-import-rejected-heading"
                className="text-[11px] font-medium text-muted-foreground"
              >
                {t('holdBack.csv.rejectedCount', { count: rejected.length })}
              </p>
              <ErrorTable
                errors={rejected}
                columns={columns}
                labelledById="hold-back-import-rejected-heading"
                maxHeight="14rem"
                filename={buildHoldBackFailedRowsFilename(
                  new Date().toISOString().slice(0, 10)
                )}
              />
            </div>
          )}

          {/* Staging is not saving. Without this line the dialog reads as if
              pressing Add to grid has already taken stock off the storefront. */}
          {!fileError && (
            <p className="text-xs text-muted-foreground">{t('holdBack.csv.stageNote')}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="h-8 gap-1.5 px-3 text-xs"
            onClick={() => onOpenChange(false)}
          >
            {t('holdBack.cancel')}
          </Button>
          {!canStage ? (
            // The primary slot carries the only move that helps. A disabled
            // "Add to grid" here would be a dead end wearing the CTA's clothes.
            // Covers both a hard file error and a file that parsed but staged
            // zero rows — either way there is nothing to add.
            <Button
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={onChooseAnotherFile}
            >
              {t('holdBack.csv.chooseAnotherFile')}
            </Button>
          ) : (
            <Button
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={() => onStage(cells)}
            >
              {t('holdBack.csv.stage')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
